import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkflowTracker = ({ workflowId, authToken }) => {
  const [workflow, setWorkflow] = useState(null);
  const [steps, setSteps] = useState([]);
  const [polling, setPolling] = useState(false);

  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const fetchWorkflow = useCallback(async () => {
    if (!workflowId) return;
    try {
      const res = await axios.get(`${API}/workflow/${workflowId}`, { headers });
      const data = res.data;
      setWorkflow(data.workflow || data);
      setSteps(data.steps || []);
      const status = data.workflow?.status || data.status;
      if (status === 'in_progress' || status === 'started') {
        setPolling(true);
      } else {
        setPolling(false);
      }
    } catch {
      setPolling(false);
    }
  }, [workflowId, authToken]);

  useEffect(() => { fetchWorkflow(); }, [fetchWorkflow]);

  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(fetchWorkflow, 2000);
    return () => clearInterval(interval);
  }, [polling, fetchWorkflow]);

  if (!workflowId || !workflow) return null;

  const statusIcon = {
    completed: <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>,
    failed: <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>,
    started: <div className="w-4 h-4 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />,
    in_progress: <div className="w-4 h-4 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />,
  };

  const progress = workflow.progress_percent || 0;

  return (
    <div data-testid="workflow-tracker" className="bg-slate-800/50 border border-slate-700/30 rounded-lg p-3 mt-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-400 font-medium">Processing your request...</span>
        <span className="text-xs text-amber-400">{progress}%</span>
      </div>
      {/* Progress bar */}
      <div className="w-full h-1.5 bg-slate-700 rounded-full mb-3">
        <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>
      {/* Steps */}
      {steps.length > 0 && (
        <div className="space-y-1.5">
          {steps.map((step, i) => (
            <div key={step.step_id || i} data-testid={`workflow-step-${i}`} className="flex items-center gap-2">
              {statusIcon[step.status] || <div className="w-4 h-4 rounded-full bg-slate-600" />}
              <span className="text-xs text-slate-300">{step.step_name || step.tool_name}</span>
              {step.duration_ms && <span className="text-[10px] text-slate-500 ml-auto">{step.duration_ms}ms</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkflowTracker;
